/**
 * CSS scroll-snap strips + same tap vs touch-move activation as ProductCardCarouselStack; cart/wishlist when active.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IoCartOutline, IoCheckmark } from 'react-icons/io5';
import { ReactComponent as HeartImg } from '../../Components/images/assets/heart.svg';
import axios from 'axios';
import { useProfile } from '../../Contexts/ProfileContext';
import { useToast } from '../../Contexts/ToastContext';
import { useContextData } from '../../ApiContext';
import { MOB_PRODUCTS_PAGE_SIZE } from '../../Slider/SliderMobNew';
import { ProductGridSkeleton } from '../../Components/images/Mob/ProductGridSkeleton';
import { fetchMobProductsFirstPage } from './mobProductsFirstPage';
import './MobProductsScrollSnapPage.scss';

const AUTO_SCROLL_MS = 2800;
const PAUSE_AFTER_INTERACTION_MS = 6000;
/** Same as ProductCardCarouselStack — clear tap → PDP; past slop → activate strip + movement. */
const VIEWPORT_TAP_SLOP_PX = 12;

function getGuestCartProductIds() {
    try {
        const raw = localStorage.getItem('guestCart');
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return (arr || []).map((i) => String(i.product_id));
    } catch {
        return [];
    }
}

function ScrollSnapStrip({
    urls,
    productId,
    isActive,
    stripLocked,
    stripRef,
    onPauseInteraction,
    onSlideIndexChange,
    gridIndex,
}) {
    const indexRef = useRef(0);
    const pausedUntilRef = useRef(0);
    const programmaticScrollRef = useRef(false);
    const autoplay = isActive && urls.length > 1;

    useEffect(() => {
        if (!autoplay) return;

        const tick = () => {
            if (Date.now() < pausedUntilRef.current) return;
            const strip = stripRef.current;
            if (!strip) return;
            const w = strip.clientWidth;
            if (w <= 0) return;
            const next = (indexRef.current + 1) % urls.length;
            indexRef.current = next;
            programmaticScrollRef.current = true;
            strip.scrollTo({ left: next * w, behavior: 'smooth' });
            window.setTimeout(() => {
                programmaticScrollRef.current = false;
            }, 500);
        };

        const id = window.setInterval(tick, AUTO_SCROLL_MS);
        return () => window.clearInterval(id);
    }, [autoplay, urls.length, productId, stripRef]);

    const pauseAutoplay = useCallback(() => {
        pausedUntilRef.current = Date.now() + PAUSE_AFTER_INTERACTION_MS;
        onPauseInteraction?.();
    }, [onPauseInteraction]);

    const syncIndexFromScroll = (el) => {
        const w = el.clientWidth;
        if (w <= 0) return;
        const idx = Math.round(el.scrollLeft / w) % urls.length;
        indexRef.current = idx;
        onSlideIndexChange?.(idx);
    };

    /** When the strip becomes interactive (activated), start from the first slide. */
    useEffect(() => {
        if (stripLocked || urls.length <= 1) return;
        const strip = stripRef.current;
        if (!strip) return;
        const id = window.requestAnimationFrame(() => {
            strip.scrollTo({ left: 0, behavior: 'auto' });
            indexRef.current = 0;
            onSlideIndexChange?.(0);
        });
        return () => window.cancelAnimationFrame(id);
    }, [stripLocked, productId, urls.length, stripRef, onSlideIndexChange]);

    const firstImgLoading = typeof gridIndex === 'number' ? (gridIndex < 4 ? 'eager' : 'lazy') : 'eager';

    return (
        <div
            ref={stripRef}
            className={`mob-ss-card__strip${stripLocked ? ' mob-ss-card__strip--locked' : ''}`}
            aria-label={
                autoplay
                    ? 'Product photos — auto-advancing; swipe to browse'
                    : 'Product photos — tap for details or swipe to browse images'
            }
            onTouchStart={autoplay ? pauseAutoplay : undefined}
            onScroll={(e) => {
                const el = e.currentTarget;
                syncIndexFromScroll(el);
                if (!autoplay) return;
                if (!programmaticScrollRef.current) {
                    pauseAutoplay();
                }
            }}
        >
            {urls.map((url, idx) => (
                <img
                    key={`${productId}-${idx}`}
                    className="mob-ss-card__img"
                    src={url}
                    alt=""
                    loading={idx === 0 ? firstImgLoading : 'lazy'}
                    draggable={false}
                />
            ))}
        </div>
    );
}

function MobScrollSnapCard({ product, isActive, onActivate, gridIndex }) {
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();
    const { id, urls: images = [], title, price, brand, mrp, rating, reviewCount } = product;
    const stripRef = useRef(null);
    const [slideDotIndex, setSlideDotIndex] = useState(0);
    const onSlideIndexChange = useCallback((idx) => {
        setSlideDotIndex(idx);
    }, []);

    const [addingToCart, setAddingToCart] = useState(null);
    const [addedToCart, setAddedToCart] = useState(() => {
        const isGuest = typeof localStorage !== 'undefined' && localStorage.getItem('guestUser') === 'true';
        if (isGuest && getGuestCartProductIds().includes(String(id))) return String(id);
        return null;
    });
    const [addingToWishlist, setAddingToWishlist] = useState(null);
    const [addedToWishlist, setAddedToWishlist] = useState(null);

    const canExplore = images.length > 1;
    const showActions = isActive || images.length <= 1;
    const stripLocked = canExplore && !isActive;

    useEffect(() => {
        const isGuest = typeof localStorage !== 'undefined' && localStorage.getItem('guestUser') === 'true';
        if (!isGuest) return;
        const check = () => {
            if (getGuestCartProductIds().includes(String(id))) setAddedToCart(String(id));
        };
        check();
        window.addEventListener('guestCartUpdated', check);
        return () => window.removeEventListener('guestCartUpdated', check);
    }, [id]);

    const handleActivate = useCallback(() => {
        onActivate(id);
    }, [id, onActivate]);

    /**
     * Inactive + multi-image: touch past slop → activate (autoplay + scroll); clear tap → synthetic click → handleCardClick → PDP.
     */
    useEffect(() => {
        const el = stripRef.current;
        if (!el || isActive || !canExplore) return;

        let sx = 0;
        let sy = 0;
        let maxExcursion = 0;
        let carouselStarted = false;

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) return;
            sx = e.touches[0].clientX;
            sy = e.touches[0].clientY;
            maxExcursion = 0;
            carouselStarted = false;
        };

        const onTouchMove = (e) => {
            if (e.touches.length !== 1) return;
            const x = e.touches[0].clientX;
            const y = e.touches[0].clientY;
            const d = Math.hypot(x - sx, y - sy);
            if (d > maxExcursion) maxExcursion = d;
            if (!carouselStarted && maxExcursion > VIEWPORT_TAP_SLOP_PX) {
                carouselStarted = true;
                handleActivate();
            }
        };

        const onTouchEnd = (e) => {
            if (e.changedTouches.length !== 1) return;
            const t = e.changedTouches[0];
            const endDist = Math.hypot(t.clientX - sx, t.clientY - sy);
            const furthest = Math.max(maxExcursion, endDist);
            if (!carouselStarted && furthest > VIEWPORT_TAP_SLOP_PX) {
                carouselStarted = true;
                handleActivate();
            }
            if (carouselStarted) {
                e.preventDefault();
            }
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('touchend', onTouchEnd, { passive: false });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [isActive, canExplore, handleActivate]);

    const handleCardClick = () => {
        navigate(`/specific/${id}`);
    };

    const handleAddToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (addedToCart === String(id)) {
            showToast({ message: 'Already in cart' });
            return;
        }
        setAddingToCart(id);
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
                const existingIndex = guestCart.findIndex((item) => String(item.product_id) === String(id));
                if (existingIndex >= 0) {
                    guestCart[existingIndex].selectedQty = (guestCart[existingIndex].selectedQty || 1) + 1;
                } else {
                    guestCart.push({
                        _id: `guest-cart-${Date.now()}-${id}`,
                        product_id: id,
                        urls: images,
                        title: title || 'Product',
                        price,
                        brand,
                        mrp,
                        selectedSize: 'M',
                        selectedQty: 1,
                        size: { M: 10 },
                        availableSizes: ['M'],
                        availableQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                        maxStock: 10,
                    });
                }
                localStorage.setItem('guestCart', JSON.stringify(guestCart));
                setAddedToCart(String(id));
                window.dispatchEvent(new Event('guestCartUpdated'));
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to cart',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
            } else {
                const token = localStorage.getItem('token');
                if (!token) {
                    showToast({
                        message: 'Please login to add items to cart',
                        variant: 'info',
                        action: { label: 'Login', onClick: () => navigate('/auth/login') },
                    });
                    return;
                }
                const pincode = '110001';
                await axios.post(`/cart/${id}`, { selectedSize: 'M', selectedQty: 1, pincode }, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAddedToCart(String(id));
                await fetchProfile();
                window.dispatchEvent(new Event('cartBadgePulse'));
                showToast({
                    message: 'Added to cart',
                    action: { label: 'View cart', onClick: () => navigate('/cart') },
                });
            }
        } catch (err) {
            console.error(err);
            showToast({ message: err.response?.data?.message || 'Could not add to cart. Try again.' });
        } finally {
            setAddingToCart(null);
        }
    };

    const handleAddToWishlist = async (e) => {
        e.stopPropagation();

        if (addedToWishlist === String(id)) {
            showToast({
                message: 'Already in wishlist',
                action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
            });
            return;
        }

        setAddingToWishlist(id);
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const exists = guestWishlist.some((item) => String(item.product_id) === String(id));
                if (!exists) {
                    guestWishlist.push({
                        _id: `guest-wish-${Date.now()}-${id}`,
                        product_id: id,
                        urls: images || [],
                        title: title || 'Product',
                        price,
                        brand,
                        mrp,
                        selectedSize: 'M',
                    });
                    localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
                }
                setAddedToWishlist(String(id));
                setTimeout(() => setAddedToWishlist(null), 3000);
                showToast({
                    message: 'Added to wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                setAddingToWishlist(null);
                showToast({ message: 'Please login to add items to wishlist' });
                return;
            }

            await axios.post(`/products/wish-list/${id}`, { selectedSize: 'M' }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setAddedToWishlist(String(id));
            setTimeout(() => setAddedToWishlist(null), 3000);
            await fetchProfile();
            showToast({
                message: 'Added to wishlist',
                action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
            });
        } catch (error) {
            if (error.response?.status === 400) {
                setAddedToWishlist(String(id));
                setTimeout(() => setAddedToWishlist(null), 3000);
                showToast({
                    message: 'Already in wishlist',
                    action: { label: 'View wishlist', onClick: () => navigate('/wishlist') },
                });
            } else {
                showToast({ message: 'Could not add to wishlist. Try again.' });
            }
        } finally {
            setAddingToWishlist(null);
        }
    };

    const discount = mrp != null && price != null && mrp > 0 ? Math.round((1 - price / mrp) * 100) : 0;
    const ratingValue = rating != null ? rating : 4.2;
    const ratingCountText =
        reviewCount != null
            ? reviewCount >= 1000
                ? `${(reviewCount / 1000).toFixed(1)}K`
                : reviewCount
            : '4.2K';

    const firstImage = images[0];

    return (
        <div
            className={`mob-ss-card ${isActive ? 'mob-ss-card--active' : 'mob-ss-card--inactive'}`}
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        >
            <div className="mob-ss-card__media-wrap">
                <ScrollSnapStrip
                    urls={images}
                    productId={id}
                    isActive={isActive}
                    stripLocked={stripLocked}
                    stripRef={stripRef}
                    onPauseInteraction={() => {}}
                    onSlideIndexChange={onSlideIndexChange}
                    gridIndex={gridIndex}
                />
                {firstImage && !isActive ? (
                    <div className="mob-ss-card__rating">
                        <span>{ratingValue} *</span>
                        <span>| {ratingCountText}</span>
                    </div>
                ) : null}
                {isActive && canExplore ? (
                    <div className="mob-ss-card__dots-wrap" aria-hidden>
                        <ul className="mob-ss-card__dots">
                            {images.map((_, i) => (
                                <li key={i} className={slideDotIndex === i ? 'active' : ''} />
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>

            <div className="mob-ss-card__meta" role="presentation">
                <div className="mob-ss-card__meta-row">
                    <div className="mob-ss-card__brand">{brand ?? '—'}</div>
                    {showActions ? (
                        <div className="mob-ss-card__actions-inline">
                            <button
                                type="button"
                                className={`action-cart ${addingToCart ? 'loading' : ''} ${addedToCart ? 'added' : ''}`}
                                onClick={handleAddToCart}
                                aria-label="Add to cart"
                            >
                                {addingToCart ? (
                                    <span className="spinner" />
                                ) : addedToCart ? (
                                    <IoCheckmark size="1.25em" />
                                ) : (
                                    <IoCartOutline size="1.25em" />
                                )}
                            </button>
                            <button
                                type="button"
                                className={`action-wishlist ${addingToWishlist ? 'loading' : ''} ${addedToWishlist ? 'added' : ''}`}
                                onClick={handleAddToWishlist}
                                aria-label="Add to wishlist"
                            >
                                {addingToWishlist ? (
                                    <span className="spinner" />
                                ) : addedToWishlist ? (
                                    <IoCheckmark size="1.25em" />
                                ) : (
                                    <HeartImg width="1.25em" height="1.25em" />
                                )}
                            </button>
                        </div>
                    ) : null}
                </div>
                {title ? <div className="mob-ss-card__title">{title}</div> : null}
                <div className="mob-ss-card__offer-details">
                    {price != null && <span>Rs. {price}</span>}
                    {mrp != null && mrp > 0 && <span>Rs. {mrp}</span>}
                    {discount > 0 && <span>({discount}% OFF)</span>}
                </div>
            </div>
        </div>
    );
}

export function MobProductsScrollSnapPage() {
    const { queryParams, setQueryParams } = useContextData();
    const [queryParamsInit, setQueryParamsInit] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeCardId, setActiveCardId] = useState(null);

    useEffect(() => {
        setQueryParams({
            category: [],
            subcategory: [],
            price: [0, 10000],
        });
        setQueryParamsInit(true);
    }, [setQueryParams]);

    useEffect(() => {
        if (!queryParamsInit) return;
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const mapped = await fetchMobProductsFirstPage(queryParams);
                if (!cancelled) setItems(mapped);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.response?.data?.message || e?.message || 'Failed to load');
                    setItems([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [queryParams, queryParamsInit]);

    const onActivate = useCallback((id) => {
        setActiveCardId(id);
    }, []);

    return (
        <div className="mob-ss">
            <header className="mob-ss__header">
                <Link to="/" className="mob-ss__back">
                    ← Home
                </Link>
                <h1 className="mob-ss__title">Approach: CSS scroll-snap</h1>
                <p className="mob-ss__sub">
                    Same <code>POST /products</code> as the shop grid. <strong>Clear tap</strong> on the card opens PDP;{' '}
                    <strong>touch movement</strong> on the image (multi-image) activates scrolling + autoplay and shows cart
                    / wishlist. Single-image cards show actions immediately.
                </p>
            </header>

            {loading ? (
                <ProductGridSkeleton cells={MOB_PRODUCTS_PAGE_SIZE} />
            ) : error ? (
                <p className="mob-ss__error" role="alert">
                    {error}
                </p>
            ) : (
                <div className="mob-ss__grid">
                    {items.map((product, index) => (
                        <MobScrollSnapCard
                            key={product.id}
                            product={product}
                            isActive={activeCardId != null && String(activeCardId) === String(product.id)}
                            onActivate={onActivate}
                            gridIndex={index}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
